/* eslint-disable i18next/no-literal-string */
import type { CSSProperties } from "react";
import { cn } from "~/lib/utils";
import { colors } from "~/components/landing/colors";

/**
 * Ported from bouncieweb's `LandingPageComponents/VehicleMarker.js`.
 *
 * A map "puck": a stationary circle with a rotated arrow plus an infinitely
 * pulsing ring behind it. `size` sets both width/height (px number or CSS
 * string), `rotate` is applied as a transform, `styles` merges extra inline
 * styles onto the wrapper (used by LocationAnimation for its keyframe transform).
 *
 * The pulse keyframes are defined once via a scoped <style> with a uniquely
 * named animation to avoid clashing with other ported keyframes.
 */

function Arrow() {
  return (
    <svg
      style={{
        transform: "rotate(-45deg)",
        position: "absolute",
        top: "33%",
        left: "28%",
      }}
      width="45%"
      height="45%"
      viewBox="0 0 12 12"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M11.2404 0.336818L6.34115 11.5764L4.77157 6.8057L0.00088875 5.23611L11.2404 0.336818Z"
        fill="white"
      />
    </svg>
  );
}

export default function VehicleMarker({
  size = 50,
  styles,
  className,
  rotate,
  color = colors.green,
  showArrow = true,
  delay = "0ms",
}: {
  size?: number | string;
  styles?: CSSProperties;
  /**
   * Appended to the wrapper. Needed when a caller positions the marker
   * responsively (inline `styles` can't express media queries) — see
   * `shoploaner/Visibility`.
   */
  className?: string;
  rotate?: string;
  color?: string;
  showArrow?: boolean;
  delay?: string;
}) {
  return (
    <>
      <style>{`
        @keyframes cf-vehicle-marker-pulse {
          0%   { width: 50%; height: 50%; opacity: 0; }
          10%  { opacity: 0.5; }
          100% { width: 100%; height: 100%; opacity: 0; }
        }
      `}</style>
      <div
        className={cn("relative", className)}
        style={{ width: size, height: size, transform: rotate, ...styles }}
      >
        {/* AnimatedCircle — infinitely pulsing ring */}
        <div
          className="absolute top-1/2 left-1/2 rounded-full"
          style={{
            width: "50%",
            height: "50%",
            transform: "translate(-50%, -50%)",
            background: color,
            animation: "cf-vehicle-marker-pulse 4s ease-out infinite",
            animationDelay: delay,
          }}
        />
        {showArrow && (
          <>
            {/* StationaryCircle */}
            <div
              className="absolute top-1/2 left-1/2 rounded-full border border-white"
              style={{
                width: "40%",
                height: "40%",
                transform: "translate(-50%, -50%)",
                background: color,
              }}
            />
            {/* ArrowWrapper */}
            <div
              className="absolute top-1/2 left-1/2"
              style={{
                width: "50%",
                height: "50%",
                transform: "translate(-50%, -50%)",
                position: "relative",
              }}
            >
              <Arrow />
            </div>
          </>
        )}
      </div>
    </>
  );
}
